import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from '@phosphor-icons/react';
import { useContext, useEffect, useRef, useState } from 'react';
import { ThemeContext } from '../../../../../apps/frontend/src/context/ThemeStore';
import FeatureCard from '../../ExploreQuizrr/generic/FeatureCard';
import axios from 'axios';

export default function ExploreSection() {
  const navigate = useNavigate();
  const { isDarkTheme } = useContext(ThemeContext);
  const [videoCards, setVideoCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const imgBasePath = isDarkTheme ? '/svg-explore-quizrr-dark' : '/svg-explore-quizrr';

  // shadow scroll logic
  const [showRightShadow, setShowRightShadow] = useState(false);
  const scrollContainerRef = useRef(null);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;

    if (!isScrollable) {
      setShowRightShadow(false);
      return;
    }

    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);
      const resizeObserver = new ResizeObserver(handleScroll);
      resizeObserver.observe(container);

      return () => {
        container.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [videoCards]); // Add videoCards as dependency to recalculate when cards load

  useEffect(() => {
    const fetchVideoCards = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/content/explore-quizrr/content');

        const cards = response?.data?.data || [];

        const formattedCards = cards.slice(0, 2).map((card) => ({
          title: card.title,
          description: card.description,
          duration: card.duration,
          videoUrl: card.videoUrl,
          tag: card.tag,
          iconSrc: isDarkTheme ? card.iconDarkSrc : card.iconSrc,
          isNew: false, // Since API doesn't provide isNew field, set to false
        }));

        setVideoCards(formattedCards);
      } catch (error) {
        // Set empty array on error
        setVideoCards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVideoCards();
  }, [isDarkTheme]);

  return (
    <section
      className="
    lg:rounded-[var(--rounded-xl,_12px)]
    px-3 py-4
    md:px-6 md:py-6
    lg:border lg:border-transparent lg:border-b-0
  "
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-[var(--q3-neutral-default)] md:text-2xl">Explore Quizrr</h1>
        <button
          onClick={() => navigate('/v3/explore')}
          className="flex items-center gap-1 text-sm font-semibold text-[var(--q3-accent-normal)] rounded-[6.667px] px-2.5 py-1 transition cursor-pointer md:border border-[var(--q3-stroke-normal)]"
          aria-label="View all features"
        >
          <span className="md:hidden">View All</span>
          <span className="hidden md:inline">Explore All Features</span>
          <ArrowRightIcon className="w-4 h-4 stroke-[2] hidden md:inline" />
        </button>
      </div>

      {/* Scrollable Cards Section */}
      <div className="relative">
        {/* right shadow */}
        {showRightShadow && (
          <div className="absolute right-0 top-0 bottom-0 w-8 z-3 pointer-events-none [background:linear-gradient(to_left,var(--q3-surface-default),transparent)]" />
        )}

        {/* Scroll container */}
        <div
          ref={scrollContainerRef}
          className="
            flex gap-4 overflow-x-auto lg:grid lg:grid-cols-2
            scrollbar-hide snap-x snap-mandatory
            px-1 py-1 scroll-smooth
          "
        >
          {loading ? (
            // Loading state
            <>
              <div className="snap-start flex-shrink-0 w-[85%] sm:w-[65%] md:w-[70%] [@media(min-width:1022px)]:w-auto lg:w-auto">
                <div className="h-[200px] bg-[var(--q3-surface-dim)] rounded-lg animate-pulse"></div>
              </div>
              <div className="snap-start flex-shrink-0 w-[85%] sm:w-[65%] md:w-[70%] [@media(min-width:1022px)]:w-auto lg:w-auto">
                <div className="h-[200px] bg-[var(--q3-surface-dim)] rounded-lg animate-pulse"></div>
              </div>
            </>
          ) : videoCards.length > 0 ? (
            // Render cards from API
            videoCards.map((card, index) => (
              <div
                key={index}
                className="snap-start flex-shrink-0 w-[85%] sm:w-[65%] md:w-[70%] [@media(min-width:1022px)]:w-auto lg:w-auto"
              >
                <FeatureCard
                  title={card.title}
                  description={card.description}
                  duration={card.duration}
                  videoUrl={card.videoUrl}
                  isNew={card.isNew}
                  tag={card.tag}
                  iconSrc={card.iconSrc}
                />
              </div>
            ))
          ) : (
            // Fallback if no cards are available
            <>
              <div className="snap-start flex-shrink-0 w-[85%] sm:w-[65%] md:w-[70%] [@media(min-width:1022px)]:w-auto lg:w-auto">
                <FeatureCard
                  title="Get to Know Quizrr"
                  description="A quick walkthrough of the Quizrr platform from attempting tests to checking analysis."
                  duration="3 MIN"
                  videoUrl="https://www.youtube.com/embed/rR00gN7LmSM"
                  tag="Quizrr Platform"
                  iconSrc={`${imgBasePath}/svg1.svg`}
                />
              </div>
              <div className="snap-start flex-shrink-0 w-[85%] sm:w-[65%] md:w-[70%] [@media(min-width:1022px)]:w-auto lg:w-auto">
                <FeatureCard
                  title="Make Best use of Test Analysis"
                  description="Make sense of your performance with detailed insights."
                  duration="6 MIN"
                  videoUrl="https://www.youtube.com/embed/ic0voQcHtvA"
                  tag="Test Analysis"
                  iconSrc={`${imgBasePath}/svg11.svg`}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
